import { getCookie } from "app/utils/cookie";
import { localGet } from "app/utils/LocalStorage";
import { capturedAtKey } from "app/utils/captureUTMParams";

/**
 * Attribution fields sent with every conversion request — POST account,
 * POST subscriptions/trial, POST stripe/checkout (CN-1954, extended by CN-2697).
 * A gclid is sent only while Google still honours it (90 days from the click,
 * with the capture time as the proxy); a stale or unstamped one goes as null so
 * the conversion still reaches Google on the hashed email instead of being
 * rejected whole with EXPIRED_EVENT. fbclid is never expired: Meta only fails
 * to attribute a stale one. Keys are snake_case on purpose: the request layer
 * underscores camelCase keys and leaves these untouched.
 */
export type ClickIdParams = {
  gclid: string | null;
  /** epoch ms, first time this gclid was seen; also sent when gclid is dropped */
  gclid_captured_at: number | null;
  fbclid: string | null;
  /** epoch ms, first time this fbclid was seen */
  fbclid_captured_at: number | null;
  /** Meta browser id — the `_fbp` cookie the pixel writes, passed unmodified */
  fbp: string | null;
  /** Meta click id cookie `_fbc` — only present once the pixel has seen an fbclid */
  fbc: string | null;
};

/** Google's click-through window for a gclid, measured here from its capture time. */
export const GCLID_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const storedString = (key: string): string | null => localGet(key) || null;

const storedEpochMs = (key: string): number | null => {
  const value = Number(localGet(key));
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * Compared against the same browser clock that wrote the stamp, so clock skew
 * cancels out. The stored id is left in place on purpose: coachnow.com re-appends
 * the same gclid from its cookie, and captureUTMParams only restamps a changed id.
 */
const freshGclid = (capturedAt: number | null): string | null => {
  if (capturedAt === null || Date.now() - capturedAt > GCLID_MAX_AGE_MS) {
    return null;
  }
  return storedString("gclid");
};

export const getClickIdParams = (): ClickIdParams => {
  const gclidCapturedAt = storedEpochMs(capturedAtKey("gclid"));

  return {
    gclid: freshGclid(gclidCapturedAt),
    gclid_captured_at: gclidCapturedAt,
    fbclid: storedString("fbclid"),
    fbclid_captured_at: storedEpochMs(capturedAtKey("fbclid")),
    fbp: getCookie("_fbp") || null,
    fbc: getCookie("_fbc") || null,
  };
};
